import type { LucideIcon } from "lucide-react";
import { ChevronRight } from "lucide-react";
import { cn } from "@eduviaos/ui-kit";
import { SectionCard } from "../Section";

export interface QuickActionItem {
  id: string;
  label: string;
  description: string;
  icon: LucideIcon;
  tint: "violet" | "orange" | "green" | "blue";
  badge?: number;
  /**
   * What the badge count means, e.g. "2 new reports". Without it the button
   * reads as a bare number followed by the label — the Chrome a11y tree on
   * stage announced "1 प्रगति रिपोर्ट अपना प्रदर्शन देखें".
   */
  badgeLabel?: string;
  onClick: () => void;
}

const TINTS: Record<QuickActionItem["tint"], string> = {
  violet: "bg-primary/12 text-primary",
  orange: "bg-orange-500/12 text-orange-600 dark:text-orange-400",
  green: "bg-emerald-500/12 text-emerald-600 dark:text-emerald-400",
  blue: "bg-sky-500/12 text-sky-600 dark:text-sky-400",
};

/**
 * A fixed 4-column track leaves a lone action (read-for-pleasure plans only get
 * "Log reading") as a 212px sliver with two words per line, so the track count
 * follows the number of actions.
 */
const COLUMN_CLASS: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-2 md:grid-cols-3",
};

export function QuickActionGrid({ items }: { items: QuickActionItem[] }) {
  if (items.length === 0) return null;

  return (
    <div
      className={cn(
        "grid gap-3 lg:gap-4",
        COLUMN_CLASS[items.length] ?? "grid-cols-2 md:grid-cols-4",
      )}
    >
      {items.map((item) => {
        const Icon = item.icon;
        const hasBadge = item.badge != null && item.badge > 0;
        return (
          <button
            key={item.id}
            type="button"
            onClick={item.onClick}
            aria-label={
              hasBadge && item.badgeLabel
                ? `${item.label} — ${item.description} — ${item.badgeLabel}`
                : undefined
            }
            className="group text-left transition-transform duration-150 ease-out active:scale-[0.98] motion-reduce:transition-none"
          >
            <SectionCard className="group-hover:bg-muted/30 flex h-full items-center gap-3 p-3.5 transition-colors duration-150 sm:p-4">
              <span
                className={cn(
                  "relative grid size-11 shrink-0 place-items-center rounded-xl",
                  TINTS[item.tint],
                )}
              >
                <Icon className="size-5" aria-hidden="true" />
                {hasBadge ? (
                  <span
                    aria-hidden="true"
                    className="bg-primary text-primary-foreground absolute -right-1 -top-1 grid min-w-[1.125rem] place-items-center rounded-full px-1 text-[0.62rem] font-bold tabular-nums"
                  >
                    {item.badge}
                  </span>
                ) : null}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold leading-tight">{item.label}</p>
                <p className="text-muted-foreground mt-0.5 text-[0.7rem] leading-snug sm:text-xs">
                  {item.description}
                </p>
              </div>
              <ChevronRight
                className="text-muted-foreground size-4 shrink-0 opacity-60 transition-opacity group-hover:opacity-100"
                aria-hidden="true"
              />
            </SectionCard>
          </button>
        );
      })}
    </div>
  );
}
